// handoff: the near sprites ⇄ far canopy volume hand-off (gs-sprite-handoff;
// APP_FORESTVOL §4b) — the ONE rule both draws apply, its CPU reference and
// its GLSL (forestvol.js and the trees.js sprite patch include HANDOFF_GLSL;
// the reference functions are the GLSL line for line — change both or
// neither). Pure (no three.js): check-forest imports it.
//
//   d     the fragment's horizontal distance to the eye (cameraPosition: each
//         XR eye its own), the volume at its hit, the sprite at its fragment
//   N, F  the sprite range (uNear) and the fade band (uFade): N = min(live
//         gensurf sprites.view_m, the Forest's cap radius — the largest
//         radius whose crowns fit renderconfig.trees.render_cap), F = live
//         sprites.fade_m
//   f     clamp((d − (N − F)) / F, 0, 1): the volume's share of the band
//   thr   the TREE's threshold (tree-midband w36, 2026-11-03): a hash of its
//         trunk's world xz on a 0.25 m grid (treeThrRef / hoTreeThr, [0, 1)),
//         read at its TRUNK distance t — object-anchored: a sprite tree keeps
//         or drops WHOLE, the same in every pixel, eye and frame. The old
//         per-pixel screen-door (interleaved gradient noise of gl_FragCoord,
//         ditherRef / hoDither: still THE COVER's noise in forestvol) made the
//         band a cloud of dots on the Quest (box probe: band speckle 7.5x a
//         supersampled reference on MSAA 4/2/0; APP_FORESTVOL §4b)
//   tile  a canopy tile is resident under the fragment (forestvol's
//         indirection): without one the volume has nothing to draw, so the
//         sprite stays whole there
//   volume keeps  ⇔ tile ∧ d ≥ N − F − HO_PAD_M   (WHOLE across the band, no
//                 dither; HO_PAD_M nearer: a dropped tree whose trunk is just
//                 past N − F reaches up to its half width nearer)
//   sprite keeps  ⇔ t < N ∧ (¬tile ∨ thr ≥ f(t))
//   volume depth  pushed back along its ray by HO_PUSH_M × (1 − f(d)) in the
//                 band (hoPush), so a kept sprite tree stands IN FRONT of the
//                 canopy surface that also holds its crown
// so no pixel of the band is ever a hole (the volume is whole where it has a
// tile, the sprite whole where it has none), each tree fades as one object
// across the band (its share linear in t), and from N on the volume alone at
// its true depth (push 0). The uniforms are ONE set of objects (HANDOFF):
// forestvol's material and every sprite material hold the same {value}, so a
// frame never sees two ranges.
//
// THE OPTIC PASS (zoom-crowns; focus.setPass brackets every optic / armed
// view render): on a focus tier with a trees block, trees.js draws the
// crowns inside the zoom wedge as apex-facing quads on the focus layer out to
// the OPTIC range No (opticRange: where a ref_h_m crown is still min_px tall
// in the optic target at the zoom, bounded by range_m and the wedge's cap);
// during the pass N = No, so the volume steps back to No by the same
// rule. uSplit partitions the crowns by TRUNK distance t (no crown twice):
//   near sprites (chunks, hero fans)  t ≤ uSplit
//   zoom quads                        uSplit < t ≤ uNear + HO_PAD_M
// naked pass (or no zoom set): uSplit = N + HO_PAD_M — the near rule as
// before; optic pass with a zoom set: uNear = No, uSplit = the naked N.
//
// The sprites' light (where a canopy tile is resident): the canopy tile's B
// (R sky-vis, G sun-vis, B snow-catch; canopygen.js) gives the crown's E in
// the bake's model, relative to an open, unshadowed crown top:
//   k = (sky·B.r + sun·max(L.y, 0)·B.g) / (sky + sun·max(L.y, 0))   (per channel)
// ramped in over w = smoothstep(N − 2F, N − F, d), so through the band the
// sprites carry the volume's light and snow; nearer (or with no tile) k = 1:
// the sprite's own painted light.

export const HANDOFF = { uNear: { value: 0 }, uFade: { value: 0 }, uSplit: { value: 0 } };
// a sprite whose trunk lies farther than N + HO_PAD_M has no fragment nearer
// than N (half the widest sprite: 31.9 m × 1.15 / 2 < 20 m): its vertices
// collapse (the cap counts the rest; trees.js)
export const HO_PAD_M = 20;
// the band's volume depth push at f = 0 (m along the ray): more than a crown's
// radius (the canopy surface stands that far in front of the sprite's trunk
// plane), so a kept sprite tree is never cut by the canopy that holds it
export const HO_PUSH_M = 12;

// the live view (forestvol._applyLive) and the Forest's cap radius
// (trees.js _cap); N = their min. Infinity cap = no cap. optic = the
// committed zoom set's No (trees.js; 0 = none), pass = inside focus.setPass
const S = { view: 0, fade: 0, cap: Infinity, optic: 0, pass: false };
function apply() {
  const n = nakedNear();
  const zoom = S.pass && S.optic > n;
  HANDOFF.uNear.value = zoom ? S.optic : n;
  HANDOFF.uFade.value = Math.max(0, Math.min(S.fade, HANDOFF.uNear.value));
  HANDOFF.uSplit.value = zoom ? n : n + HO_PAD_M;
}
export function setHandoffView(view_m, fade_m) { S.view = +view_m || 0; S.fade = +fade_m || 0; apply(); }
export function setSpriteCap(r) { S.cap = r > 0 ? r : Infinity; apply(); }
export function setOpticNear(r) { S.optic = r > 0 ? r : 0; apply(); }
export function setHandoffPass(on) { if (S.pass !== !!on) { S.pass = !!on; apply(); } }
// the naked-eye N (every pass but an optic one with a zoom set)
export function nakedNear() { return Math.max(0, Math.min(S.view, S.cap)); }
apply();
// the optic range before the cap: the distance at which a crown refH m tall
// is minPx pixels of an rtPx-tall target at vertical fov vfovDeg (the
// focusRanges idiom, game/lodmath.js), clamped to rangeM; monotone in zoom
export function opticRange(vfovDeg, rtPx, minPx, refH, rangeM) {
  const pxRad = (vfovDeg * Math.PI / 180) / rtPx;
  return Math.min(rangeM, refH / (minPx * Math.max(1e-9, pxRad)));
}
// the zoom set's No: min(opticRange, the wedge's cap radius — capRadius on
// the histogram of the wedge's crowns past the split) − slack (how far the
// eye may stray from the set's build position); a No not past the naked N
// means no zoom set (0)
export function opticNear(range, capR, naked, slack = 0) {
  const r = Math.min(range, capR) - slack;
  return r > naked ? r : 0;
}
// the zoom set's plan (trees.js Forest._zoomJob): dist[0..n) = the eye
// distances of the crowns inside the wedge's sector (any order). The set is
// built at an eye that may then stray ZOOM_SLACK_M before a rebuild, so it
// holds every crown past lo = naked − 2 × slack and up to lim = No + slack +
// HO_PAD_M; No = opticNear(range, the cap radius of their histogram, naked,
// slack) — so the crowns within lim number ≤ cap (nearest first)
export const ZOOM_SLACK_M = 32;
export const ZOOM_BIN_M = 16;
export const zoomLo = (naked) => Math.max(0, naked - 2 * ZOOM_SLACK_M);
export function zoomPlan(dist, n, naked, range, cap) {
  const lo = zoomLo(naked), R = range + HO_PAD_M;
  const hist = new Uint32Array(Math.ceil(R / ZOOM_BIN_M) + 2);
  let total = 0;
  for (let k = 0; k < n; k++) {
    const d = dist[k];
    if (d <= lo || d > R) continue;
    hist[Math.ceil(d / ZOOM_BIN_M)]++;
    total++;
  }
  const no = opticNear(range, capRadius(hist, ZOOM_BIN_M, cap, total), naked, ZOOM_SLACK_M);
  return { no, lo, R, lim: no ? no + ZOOM_SLACK_M + HO_PAD_M : 0 };
}
// the trunk partition (the sprite patch's VS, trees.js): a crown at trunk
// distance t is a near sprite or a zoom quad, never both, never neither (t ≤
// uNear + HO_PAD_M)
export const nearLiveRef = (t, split) => t <= split;
export const zoomLiveRef = (t, split, near) => t > split && t <= near + HO_PAD_M;
// the cap radius from a histogram of trunk distances (hist[b] = the crowns
// with ceil(d / binM) = b, total = their sum): the largest r whose crowns
// with trunk ≤ r + HO_PAD_M (the sprites drawn at all) number ≤ cap;
// Infinity when every crown fits
export function capRadius(hist, binM, cap, total) {
  if (total <= cap) return Infinity;
  let acc = 0, b = 0;
  while (b < hist.length && acc + hist[b] <= cap) acc += hist[b++];
  return Math.max(0, (b - 1) * binM - HO_PAD_M);
}
// THE SHRUB PAIR (gs-family-shrub) — INERT until phase S2: the same rule
// between the near shrub quads (shrubs.js) and the shrubs' low volume (the
// ground tree's shrub tap, shrubtap.js — not drawn yet: the ground-shader hook
// is S2). Today shrubs.js sets N = 1e9, F = 0 and passes tile = false, so
// every shrub fragment keeps: exactly today's draw (the near set and the
// scope wedge's far shrubs share the material). S2 sets N = shrubs.view_m,
// F = live shrub.fade_m and the tile from the shrub indirection.
export const HANDOFF_SHRUB = { uNear: { value: 0 }, uFade: { value: 0 } };
export function setShrubHandoff(view_m, fade_m) {
  HANDOFF_SHRUB.uNear.value = Math.max(0, +view_m || 0);
  HANDOFF_SHRUB.uFade.value = Math.max(0, Math.min(+fade_m || 0, HANDOFF_SHRUB.uNear.value));
}

// the band [N − F, N] now (canopygen wants every tile reaching past N − F)
export function handoffBand() { return [HANDOFF.uNear.value - HANDOFF.uFade.value, HANDOFF.uNear.value]; }

// ---- the CPU reference
const fract = (v) => v - Math.floor(v);
// the screen-door threshold of pixel (px, py) — gl_FragCoord floored
export function ditherRef(px, py) {
  return fract(52.9829189 * fract(Math.floor(px) * 0.06711056 + Math.floor(py) * 0.00583715));
}
export function fadeRef(d, near, fade) {
  return fade > 0 ? Math.min(1, Math.max(0, (d - (near - fade)) / fade)) : (d >= near ? 1 : 0);
}
// the tree's threshold: its trunk's world xz (m) on a 0.25 m grid, mod 4096 m
export function treeThrRef(x, z) {
  const m = (v) => v - 4096 * Math.floor(v / 4096);
  return ditherRef(m(x) * 4, m(z) * 4);
}
export function volKeepRef(d, near, fade, tile) {
  return !!tile && d >= near - fade - HO_PAD_M;
}
// t = the trunk's distance, thr = treeThrRef of the trunk
export function spriteKeepRef(t, thr, near, fade, tile) {
  return t < near && (!tile || thr >= fadeRef(t, near, fade));
}
// the volume's depth push (m along the ray) at d, where it keeps
export function pushRef(d, near, fade) {
  return HO_PUSH_M * (1 - fadeRef(d, near, fade));
}
const smooth = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
// the canopy light's weight on a sprite fragment (light and snow alike)
export function hoWeightRef(d, near, fade, tile) {
  return !tile ? 0 : fade > 0 ? smooth(near - 2 * fade, near - fade, d) : (d >= near ? 1 : 0);
}
// the sprite's light multiplier (per channel): B = [sky-vis, sun-vis] 0..1,
// sky / sun = meta.tex.light colours, ly = the sun direction's y, w = hoWeightRef
export function spriteLightRef(B, sky, sun, ly, w) {
  const s = Math.max(ly, 0);
  return [0, 1, 2].map((c) => {
    const k = (sky[c] * B[0] + sun[c] * s * B[1]) / Math.max(1e-6, sky[c] + sun[c] * s);
    return 1 + (k - 1) * w;
  });
}

// ---- the GLSL (uniform float uNear, uFade declared by the includer)
// the tree's threshold alone (the sprite VS: no uniforms)
export const HO_TREE_GLSL = `
float hoDither(vec2 p) { return fract(52.9829189 * fract(dot(floor(p), vec2(0.06711056, 0.00583715)))); }
float hoTreeThr(vec2 xz) { return hoDither(mod(xz, 4096.0) * 4.0); }
`;
export const HANDOFF_GLSL = `${HO_TREE_GLSL}float hoFade(float d) { return uFade > 0.0 ? clamp((d - (uNear - uFade)) / uFade, 0.0, 1.0) : (d >= uNear ? 1.0 : 0.0); }
bool hoVolKeep(float d, bool tile) { return tile && d >= uNear - uFade - ${HO_PAD_M.toFixed(1)}; }
bool hoSpriteKeep(float t, float thr, bool tile) { return t < uNear && (!tile || thr >= hoFade(t)); }
float hoPush(float d) { return ${HO_PUSH_M.toFixed(1)} * (1.0 - hoFade(d)); }
float hoWeight(float d, bool tile) {
  return !tile ? 0.0 : uFade > 0.0 ? smoothstep(uNear - 2.0 * uFade, uNear - uFade, d) : step(uNear, d);
}
vec3 hoLight(vec2 B, vec3 sky, vec3 sun, float ly, float w) {
  float s = max(ly, 0.0);
  vec3 k = (sky * B.x + sun * (s * B.y)) / max(sky + sun * s, vec3(1e-6));
  return 1.0 + (k - 1.0) * w;
}
`;
